// Workspace → Tasks: the org's own to-do list, shared by the Admin, Seller and
// Supply shells. Open work first, then by due date; urgent before normal.
// The left menu (TasksSidebar) picks what is shown through `?view=`.
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { CalendarDays, List, Plus } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { EmptyState } from '../../components/ui/empty-state'
import { Input } from '../../components/ui/input'
import { PageFrame } from '../../components/ui/page-frame'
import { PageHeader } from '../../components/ui/page-header'
import { Select } from '../../components/ui/select'
import { USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { useMe } from '../../lib/useMe'
import { cn } from '../../lib/utils'
import { easternToday } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import {
  createTask,
  listAssignees,
  listFolders,
  listTasks,
  staffTaskKeys,
  StaffTaskApiError,
  updateTask,
  type StaffTask,
  type TaskScope,
  type TaskStatus,
} from './api'
import { parseView, projectRoute, type TaskShell } from './lib'
import { SIDEBAR_HEADER, TasksLayout } from './TasksSidebar'
import { AutomationsTab } from './AutomationsTab'
import { ProjectFormDialog } from './ProjectFormDialog'
import { ProjectsTab } from './ProjectsTab'
import { groupByDue, type DueBucket } from './dates'
import { useHiddenColumns, type SortState } from './columns'
import { ColumnsMenu, TaskTable } from './TaskTable'
import { TaskCalendar } from './TaskCalendar'
import { TaskFormDialog } from './TaskFormDialog'
import { AssigneePicker } from './AssigneePicker'
import { TaskMobileList } from './TaskMobileList'
import { useMobileViewport } from './useMobileViewport'
import './StaffTasksPage.css'

const PAGE_SIZE = 100

type StatusFilter = '' | 'open' | TaskStatus

// List filters live in the URL (`?status=&assignee=&overdue=&q=`) so they
// survive opening a task and coming back. Defaults are left out of the URL.
const STATUS_VALUES = ['todo', 'in_progress', 'done'] as const

function parseStatus(raw: unknown): StatusFilter {
  if (raw === 'any') return ''
  return STATUS_VALUES.find((value) => value === raw) ?? 'open'
}

export function StaffTasksPage({ shell }: { shell: TaskShell }) {
  const t = useT(staffTasksI18n)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const taskSearch = useSearch({ strict: false }) as {
    view?: unknown
    tab?: unknown
    status?: unknown
    assignee?: unknown
    overdue?: unknown
    q?: unknown
  }
  // Team members without task-admin rights start on their own tasks.
  const { me, hasCapability } = useMe()
  // Opening and changing tasks needs task-admin rights (staff_task.manage).
  const canManage = hasCapability('staff_task.manage')
  const defaultView = canManage ? 'all' : 'mine'
  const mobile = useMobileViewport()
  const [quickTitle, setQuickTitle] = useState('')
  const active = parseView(taskSearch, defaultView)
  const defaultReady = !USE_REAL_API || me !== undefined || taskSearch.view !== undefined || taskSearch.tab !== undefined
  const person = active.startsWith('person:') ? active.slice('person:'.length) : null
  const folder = active.startsWith('folder:') ? active.slice('folder:'.length) : null
  const projectView = active === 'projects' || folder !== null
  const taskTab = !projectView && active !== 'automations'
  const compactAdminTasks = shell === 'admin' && taskTab
  const scope: TaskScope = active === 'mine' || active === 'created' ? active : 'all'
  const [ruleOpen, setRuleOpen] = useState(false)
  const [projectOpen, setProjectOpen] = useState<'blank' | 'template' | null>(null)
  const status = parseStatus(taskSearch.status)
  const assignee = taskSearch.assignee == null ? '' : String(taskSearch.assignee)
  const overdue = taskSearch.overdue === true || taskSearch.overdue === 'true'
  const [search, setSearch] = useState(taskSearch.q == null ? '' : String(taskSearch.q))
  const setFilter = (patch: Record<string, string | true | undefined>) =>
    void navigate({ search: (prev: object) => ({ ...prev, ...patch }), replace: true } as never)
  const [createOpen, setCreateOpen] = useState(false)
  const [editingAssignees, setEditingAssignees] = useState<StaffTask | null>(null)
  const [draftAssigneeIds, setDraftAssigneeIds] = useState<string[]>([])
  const [view, setView] = useState<'list' | 'calendar'>('list')
  const [collapsed, setCollapsed] = useState<Set<DueBucket>>(() => new Set(['done']))

  const filters = {
    scope,
    status: status || undefined,
    assignee_user_id: person ?? (scope === 'mine' ? undefined : assignee || undefined),
    overdue,
    q: search.trim() || undefined,
  }
  const [sort, setSort] = useState<SortState>(null)
  const [hidden, toggleColumn] = useHiddenColumns()
  const params = {
    ...filters,
    sort: sort?.key,
    dir: sort?.dir,
    limit: PAGE_SIZE,
  }
  const tasks = useQuery({
    queryKey: staffTaskKeys.list(params),
    queryFn: () => listTasks(params),
    enabled: view === 'list' && taskTab && defaultReady,
  })
  const assignees = useQuery({ queryKey: staffTaskKeys.assignees, queryFn: listAssignees })
  const folders = useQuery({ queryKey: staffTaskKeys.folders, queryFn: listFolders, enabled: !!folder })
  const personName = assignees.data?.items.find((p) => p.user_id === person)?.display_name
  const folderName =
    folder === 'none'
      ? t.sidebar.noFolder
      : folders.data?.items.find((f) => f.id === folder)?.name
  const title = person
    ? t.sidebar.personTitle(personName ?? '…')
    : folder
      ? (folderName ?? '…')
      : {
          mine: t.sidebar.myTasks,
          all: t.title,
          created: t.sidebar.createdByMe,
          projects: t.sidebar.allProjects,
          automations: t.sidebar.automations,
        }[active as 'mine' | 'all' | 'created' | 'projects' | 'automations']
  const grouped = scope === 'mine' || person !== null

  const setTaskStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: TaskStatus }) => updateTask(id, { status: next }),
    onSuccess: (_saved, { next }) => {
      toast.success(next === 'done' ? t.toasts.markedDone : t.toasts.statusSaved)
      void queryClient.invalidateQueries({ queryKey: staffTaskKeys.all })
    },
    onError: () => toast.error(t.toasts.error),
  })
  // Quick add: the title only; everything else as in the New task dialog
  // (assigned to the person opening it, or to the person whose list is open).
  // Same source as the New task dialog, so both default to the same person.
  const authMe = useQuery({ queryKey: ['auth', 'me'], queryFn: fetchMe, staleTime: 5 * 60 * 1000, enabled: canManage })
  const meId = authMe.data?.user.id
  const quickAssignee = person ?? (meId && assignees.data?.items.some((p) => p.user_id === meId) ? meId : null)
  const quickAdd = useMutation({
    mutationFn: (title: string) =>
      createTask({ title, assignee_user_ids: quickAssignee ? [quickAssignee] : [] }),
    onSuccess: () => {
      setQuickTitle('')
      toast.success(t.toasts.created)
      void queryClient.invalidateQueries({ queryKey: staffTaskKeys.all })
    },
    onError: (error) => {
      const code = error instanceof StaffTaskApiError ? error.code : undefined
      toast.error(code === 'assignee_not_in_team' ? t.toasts.notInTeam : t.toasts.error)
    },
  })
  const submitQuickAdd = (event: FormEvent) => {
    event.preventDefault()
    const title = quickTitle.trim()
    if (title && !quickAdd.isPending) quickAdd.mutate(title)
  }
  const markDone = canManage ? (task: StaffTask) => setTaskStatus.mutate({ id: task.id, next: 'done' }) : undefined
  const saveAssignees = useMutation({
    mutationFn: ({ id, ids }: { id: string; ids: string[] }) => updateTask(id, { assignee_user_ids: ids }),
    onSuccess: () => {
      setEditingAssignees(null)
      toast.success(t.toasts.saved)
      void queryClient.invalidateQueries({ queryKey: staffTaskKeys.all })
    },
    onError: (error) => {
      const code = error instanceof StaffTaskApiError ? error.code : undefined
      toast.error(code === 'assignee_not_in_team' ? t.toasts.notInTeam : t.toasts.error)
    },
  })

  const toggleGroup = (bucket: DueBucket) =>
    setCollapsed((current) => {
      const next = new Set(current)
      if (next.has(bucket)) next.delete(bucket)
      else next.add(bucket)
      return next
    })

  const filtered = status !== 'open' || overdue || !!search.trim() || !!assignee
  const items = tasks.data?.items ?? []

  return (
    <PageFrame
      data-testid="staff-tasks-page"
      className={cn(
        compactAdminTasks && 'staff-task-workspace',
        compactAdminTasks && 'staff-task-calm',
      )}
    >
      <TasksLayout shell={shell} active={{ view: active }} compact={compactAdminTasks}>
      <PageHeader
        className={SIDEBAR_HEADER}
        title={title}
        description={active === 'all' ? t.description : undefined}
        actions={
          active === 'automations' ? (
            <Button onClick={() => setRuleOpen(true)} data-testid="staff-automation-new">
              <Plus className="mr-1 h-4 w-4" aria-hidden />
              {t.automations.add}
            </Button>
          ) : projectView ? (
            <>
              <Button variant="outline" onClick={() => setProjectOpen('template')}>
                {t.projects.fromTemplate}
              </Button>
              <Button onClick={() => setProjectOpen('blank')} data-testid="staff-project-new">
                <Plus className="mr-1 h-4 w-4" aria-hidden />
                {t.projects.newProject}
              </Button>
            </>
          ) : (
            <Button onClick={() => setCreateOpen(true)} data-testid="staff-task-new">
              <Plus className="mr-1 h-4 w-4" aria-hidden />
              {t.newTask}
            </Button>
          )
        }
      />

      <div
        className={cn(
          'flex flex-col gap-3',
          compactAdminTasks && 'task-tool-panel',
          !taskTab && 'hidden',
        )}
        role={compactAdminTasks ? 'region' : undefined}
        aria-label={compactAdminTasks ? t.navLabel : undefined}
      >
        <div className={cn('flex flex-wrap gap-2', compactAdminTasks && 'task-view-row')}>
          <div className="ml-auto flex gap-1">
            {!mobile && <ColumnsMenu hidden={hidden} onToggle={toggleColumn} />}
            {(['list', 'calendar'] as const).map((value) => (
              <Button
                key={value}
                variant={view === value ? 'default' : 'outline'}
                size="sm"
                aria-pressed={view === value}
                onClick={() => setView(value)}
                data-testid={`staff-task-view-${value}`}
              >
                {value === 'list' ? (
                  <List className="mr-1 h-4 w-4" aria-hidden />
                ) : (
                  <CalendarDays className="mr-1 h-4 w-4" aria-hidden />
                )}
                {t.views[value]}
              </Button>
            ))}
          </div>
        </div>

        <div
          className={cn(
            'flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center',
            compactAdminTasks && 'task-filter-row',
          )}
        >
          <Input
            className="sm:max-w-xs"
            value={search}
            placeholder={t.filters.searchPlaceholder}
            aria-label={t.filters.searchPlaceholder}
            onChange={(e) => {
              setSearch(e.target.value)
              setFilter({ q: e.target.value || undefined })
            }}
          />
          <Select
            className="sm:w-44"
            aria-label={t.filters.status}
            value={status}
            onChange={(e) => setFilter({ status: e.target.value === '' ? 'any' : e.target.value === 'open' ? undefined : e.target.value })}
          >
            <option value="open">{t.filters.openOnly}</option>
            <option value="">{t.filters.anyStatus}</option>
            <option value="todo">{t.status.todo}</option>
            <option value="in_progress">{t.status.in_progress}</option>
            <option value="done">{t.status.done}</option>
          </Select>
          {scope !== 'mine' && !person && (
            <Select
              className="sm:w-48"
              aria-label={t.filters.assignee}
              value={assignee}
              onChange={(e) => setFilter({ assignee: e.target.value || undefined })}
            >
              <option value="">{t.filters.anyone}</option>
              {(assignees.data?.items ?? []).map((person) => (
                <option key={person.user_id} value={person.user_id}>
                  {person.display_name}
                </option>
              ))}
            </Select>
          )}
          <label className="flex items-center gap-2 text-sm text-[var(--color-text)]">
            <input
              type="checkbox"
              checked={overdue}
              onChange={(e) => setFilter({ overdue: e.target.checked ? true : undefined })}
            />
            {t.filters.overdueOnly}
          </label>
        </div>
      </div>

      {taskTab && view === 'list' && canManage && (
        <form onSubmit={submitQuickAdd} className="flex gap-2" data-testid="staff-task-quick-add">
          <Input
            value={quickTitle}
            maxLength={200}
            placeholder={t.quick.addPlaceholder}
            aria-label={t.quick.addLabel}
            disabled={quickAdd.isPending}
            onChange={(e) => setQuickTitle(e.target.value)}
          />
          <Button
            type="submit"
            variant="outline"
            size="icon"
            className="shrink-0"
            aria-label={t.quick.add}
            title={t.quick.add}
            disabled={!quickTitle.trim() || quickAdd.isPending}
          >
            <Plus className="h-4 w-4" aria-hidden />
          </Button>
        </form>
      )}

      {active === 'automations' ? (
        <AutomationsTab createOpen={ruleOpen} onCreateOpenChange={setRuleOpen} />
      ) : projectView ? (
        <ProjectsTab
          shell={shell}
          folderId={folder}
          onCreate={() => setProjectOpen('blank')}
        />
      ) : view === 'calendar' ? (
        <TaskCalendar shell={shell} filters={filters} />
      ) : tasks.isError ? (
        <p role="alert" className="text-sm text-[var(--color-danger)]">
          {t.loadError}
        </p>
      ) : !tasks.isLoading && items.length === 0 ? (
        <EmptyState
          icon="taskComplete"
          title={filtered ? t.empty.filteredTitle : t.empty.title}
          description={filtered ? undefined : t.empty.description}
          action={
            filtered ? undefined : (
              <Button onClick={() => setCreateOpen(true)}>{t.newTask}</Button>
            )
          }
        />
      ) : mobile ? (
        <TaskMobileList
          shell={shell}
          items={items}
          loading={tasks.isLoading}
          error={false}
          onRetry={() => void tasks.refetch()}
          onDone={markDone}
          groups={grouped ? groupByDue(items, easternToday()) : undefined}
          collapsed={collapsed}
          onToggleGroup={toggleGroup}
        />
      ) : (
        <TaskTable
          shell={shell}
          items={items}
          hidden={hidden}
          sort={sort}
          onSort={setSort}
          busy={tasks.isLoading}
          balancedColumns={compactAdminTasks}
          onStatus={(task, next) => setTaskStatus.mutate({ id: task.id, next })}
          onDone={markDone}
          onEditAssignees={shell === 'admin' ? (task) => {
            setEditingAssignees(task)
            setDraftAssigneeIds(task.assignees.map((person) => person.user_id))
          } : undefined}
          groups={grouped ? groupByDue(items, easternToday()) : undefined}
          collapsed={collapsed}
          onToggleGroup={toggleGroup}
        />
      )}

      <TaskFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <Dialog
        open={editingAssignees !== null}
        onOpenChange={(open) => {
          if (!open && !saveAssignees.isPending) setEditingAssignees(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.table.editAssigneesTitle}</DialogTitle>
            <DialogDescription>{editingAssignees?.title}</DialogDescription>
          </DialogHeader>
          <AssigneePicker
            people={assignees.data?.items ?? []}
            value={draftAssigneeIds}
            onChange={setDraftAssigneeIds}
            disabled={saveAssignees.isPending}
          />
          <DialogFooter>
            <Button variant="outline" type="button" disabled={saveAssignees.isPending} onClick={() => setEditingAssignees(null)}>
              {t.form.cancel}
            </Button>
            <Button
              type="button"
              disabled={saveAssignees.isPending}
              onClick={() => {
                if (editingAssignees) saveAssignees.mutate({ id: editingAssignees.id, ids: draftAssigneeIds })
              }}
            >
              {t.form.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ProjectFormDialog
        open={projectOpen !== null}
        onOpenChange={(open) => !open && setProjectOpen(null)}
        fromTemplate={projectOpen === 'template'}
        initialFolderId={folder && folder !== 'none' ? folder : undefined}
        onSaved={(detail) => void navigate(projectRoute(shell, detail.project.id))}
      />
      </TasksLayout>
    </PageFrame>
  )
}
